import { getLocalizedMessage } from '@pimo/pimo-l10n';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { styled, Switch, Stack, FormControl, FormLabel, FormGroup, FormControlLabel, Checkbox, FormHelperText, TextField, MenuItem, Box, Select, Typography, Button, Paper, Grid2, Accordion, AccordionSummary, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { PMButton } from '@pimo/pimo-components';
import { Add, DeleteOutlined, Error as Error$1, ExpandMore } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { Box as Box$1 } from '@mui/system';
import dayjs from 'dayjs';
import { useState, useId } from 'react';
import '@mdxeditor/editor/style.css';
import { MDXEditor, toolbarPlugin, UndoRedo, BoldItalicUnderlineToggles, ListsToggle, headingsPlugin, listsPlugin, quotePlugin } from '@mdxeditor/editor';
import { usePimoForm } from '@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook';

var NotificationChannel;
(function (NotificationChannel) {
    NotificationChannel["BANNER"] = "banner";
    NotificationChannel["EMAIL"] = "email";
    NotificationChannel["NOTIFICATION"] = "notification";
    NotificationChannel["TEAMS"] = "teams";
})(NotificationChannel || (NotificationChannel = {}));
function isScheduledNotification(notification, type) {
    if (type === "scheduled") {
        return true;
    }
    return false;
}
/**
 * A notification created in the browser has no real id until the backend
 * assigns one on save. This prefix marks that not-yet-persisted state so the
 * UI can give it a stable local identity (React keys, array lookups) while
 * still knowing to `POST` rather than `PUT` it, and to keep its fields
 * editable until then.
 */
const DRAFT_ID_PREFIX = "temporary-";
function createDraftNotificationId() {
    return `${DRAFT_ID_PREFIX}${Date.now()}`;
}
function isDraftNotification(notification) {
    var _a, _b;
    return (_b = (_a = notification.id) === null || _a === void 0 ? void 0 : _a.startsWith(DRAFT_ID_PREFIX)) !== null && _b !== void 0 ? _b : false;
}
/** Clears a draft's local id so saving sends it as a new row to create. */
function withoutDraftId(notification) {
    return isDraftNotification(notification)
        ? Object.assign(Object.assign({}, notification), { id: undefined }) : notification;
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

// Presentation only: never replace the persisted/dispatch trigger values with
// these categories. Shared with the automated form's future event selector.
const AUTOMATED_TRIGGER_CATEGORIES = [
    "ReportSubmitted",
    "TwoDayDeadlineReminder",
    "OneDayDeadlineReminder",
    "TodayDeadlineReminder",
    "NewReportingCycleStarted",
];
const getAutomatedTriggerCategory = (trigger) => {
    var _a;
    const identity = trigger.replace(/WithoutProjects$/, "");
    return ((_a = AUTOMATED_TRIGGER_CATEGORIES.find((category) => category === identity)) !== null && _a !== void 0 ? _a : "other");
};
const categoryOrder = (category) => category === "other"
    ? AUTOMATED_TRIGGER_CATEGORIES.length
    : AUTOMATED_TRIGGER_CATEGORIES.indexOf(category);
/** Multi-event rows belong to their first known category, regardless of array order. */
const getAutomatedNotificationOrder = (notification) => {
    var _a;
    return Math.min(AUTOMATED_TRIGGER_CATEGORIES.length, ...((_a = notification.triggers) !== null && _a !== void 0 ? _a : []).map((trigger) => categoryOrder(getAutomatedTriggerCategory(trigger))));
};
const getAutomatedTriggerLabel = (notification, language) => {
    var _a;
    const categories = [
        ...new Set(((_a = notification.triggers) !== null && _a !== void 0 ? _a : []).map(getAutomatedTriggerCategory)),
    ].sort((a, b) => categoryOrder(a) - categoryOrder(b));
    return (categories
        .map((category) => getLocalizedMessage(language, `notificationsManager.automatedTriggers.${category}`))
        .join(", ") || undefined);
};

const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 35,
    height: 18,
    padding: 0,
    marginLeft: 40,
    display: "flex",
    "&:active": {
        "& .MuiSwitch-thumb": {
            width: 15,
            height: 15,
            color: "#fff",
        },
        "& .MuiSwitch-switchBase.Mui-checked": {
            transform: "translateX(0px)",
        },
    },
    "& .MuiSwitch-switchBase": {
        padding: 2,
        "&.Mui-checked": {
            transform: "translateX(16px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
                opacity: 1,
                backgroundColor: "#6fe255",
            },
        },
    },
    "& .MuiSwitch-thumb": {
        width: 15,
        height: 15,
        borderRadius: 8,
        transition: theme.transitions.create(["width"], {
            duration: 200,
        }),
    },
    "& .MuiSwitch-track": Object.assign({ borderRadius: 20 / 2, opacity: 1, backgroundColor: "rgba(27, 18, 18, 0.22)", boxSizing: "border-box" }, theme.applyStyles("dark", {
        backgroundColor: "rgba(255,255,255,.35)",
    })),
}));

const NOTIFICTATION_CHANNELS = [
    {
        key: NotificationChannel.BANNER,
        label: "Banner",
    },
    {
        key: NotificationChannel.NOTIFICATION,
        label: "Notification",
    },
    {
        key: NotificationChannel.EMAIL,
        label: "Email",
    },
    {
        key: NotificationChannel.TEAMS,
        label: "Teams",
    },
];
const DEFAULT_CHANNELS = [
    NotificationChannel.BANNER,
    NotificationChannel.NOTIFICATION,
    NotificationChannel.EMAIL,
    NotificationChannel.TEAMS,
];

const NotificationForm = ({ notification, onChangeNotification, recipientLists, type, validationErrors, hasTeamsChannel, hasEmailChannel, oneTimeNotificationLifecycle = false, availableTriggers = [], language, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { setValue } = usePimoForm({
        appStateSlice: "editSettingsForm",
    });
    // The opt-in controls delivery presentation for both forms, not the
    // scheduled notification's recurring lifecycle.
    const supportedChannelsOnly = oneTimeNotificationLifecycle;
    const automatedPresentation = type === "scheduled" && oneTimeNotificationLifecycle;
    const triggerOptions = type === "scheduled"
        ? automatedPresentation
            ? getAutomatedTriggerOptions(notification, availableTriggers, language)
            : [...new Set(availableTriggers)].map((trigger) => ({
                value: trigger,
                label: getTriggerLabel(trigger, language),
                triggers: [trigger],
            }))
        : [];
    const filterChannel = (channel) => {
        if (supportedChannelsOnly &&
            channel !== NotificationChannel.BANNER &&
            channel !== NotificationChannel.EMAIL)
            return false;
        if (channel === NotificationChannel.TEAMS)
            return hasTeamsChannel;
        if (channel === NotificationChannel.EMAIL)
            return hasEmailChannel;
        return true;
    };
    const availableChannels = NOTIFICTATION_CHANNELS.filter(({ key }) => filterChannel(key));
    const channels = ((_a = notification.channels) !== null && _a !== void 0 ? _a : (supportedChannelsOnly ? [] : DEFAULT_CHANNELS)).filter(filterChannel);
    const isLegacyAlertOnly = supportedChannelsOnly &&
        !!notification.id &&
        !isDraftNotification(notification) &&
        ((_b = notification.channels) === null || _b === void 0 ? void 0 : _b.length) === 1 &&
        notification.channels[0] === NotificationChannel.NOTIFICATION;
    const [title, setTitle] = useState(notification.title);
    const [text, setText] = useState(notification.text);
    const isNewScheduled = type === "scheduled" && isDraftNotification(notification);
    const handleCheckChange = (event) => {
        const channel = event.target.name;
        const checked = event.target.checked;
        const channelIndex = channels.indexOf(channel);
        // One channel must be selected
        if (channels.length === 1 && channels[0] === channel) {
            return;
        }
        if (channelIndex === -1 && checked) {
            channels.push(channel);
        }
        else if (channelIndex > -1 && !checked) {
            channels.splice(channelIndex, 1);
        }
        onChangeNotification(Object.assign(Object.assign({}, notification), { 
            // Only a deliberate delivery selection changes channels. Preserve the
            // backend's internal bell membership; never expose it as a checkbox.
            channels: supportedChannelsOnly
                ? [...channels, NotificationChannel.NOTIFICATION]
                : channels }));
    };
    return (jsxs(Stack, { gap: supportedChannelsOnly ? 4 : "24px", sx: {
            borderRadius: 3,
            boxShadow: "none",
            border: "1px solid",
            borderColor: grey[300],
            p: supportedChannelsOnly ? 2 : "24px",
            m: supportedChannelsOnly ? "0 16px 16px" : "0 0 24px",
        }, children: [jsxs(FormControl, { error: Boolean(validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.channels"]), children: [jsx(FormLabel, { sx: { fontWeight: "bold", color: "#000000" }, children: getLocalizedMessage(language, "notificationsManager.notificationType") }), jsx(FormGroup, { sx: { display: "flex", flexDirection: "row", gap: "16px" }, children: availableChannels.map((type) => (jsx(FormControlLabel, { labelPlacement: "start", control: jsx(Checkbox, { checked: channels === null || channels === void 0 ? void 0 : channels.some((channel) => channel === type.key), onChange: handleCheckChange, name: type.key }), label: getLocalizedMessageOrFallback$1(language, `notificationsManager.channel.${type.key}`, type.label) }, `${notification.id}-channel-${type.key}`))) }), isLegacyAlertOnly && (jsx(FormHelperText, { children: getLocalizedMessage(language, "notificationsManager.legacyAlertOnly") })), (validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.channels"]) && (jsx(FormHelperText, { children: validationErrors["notification.channels"].message }))] }), type === "scheduled" && (jsxs(FormControl, { children: [jsx(FormLabel, { sx: { fontWeight: "bold", color: "#000000" }, children: getLocalizedMessage(language, "notificationsManager.trigger") }), jsx(TextField, { select: automatedPresentation || availableTriggers.length > 0, value: automatedPresentation && ((_c = notification.triggers) === null || _c === void 0 ? void 0 : _c[0])
                            ? getAutomatedTriggerCategory(notification.triggers[0])
                            : ((_e = (_d = notification.triggers) === null || _d === void 0 ? void 0 : _d[0]) !== null && _e !== void 0 ? _e : ""), disabled: !isNewScheduled, placeholder: getLocalizedMessage(language, "notificationsManager.triggerPlaceholder"), slotProps: Object.assign(Object.assign({}, (automatedPresentation
                            ? {
                                select: {
                                    renderValue: () => getAutomatedTriggerLabel(notification, language),
                                },
                            }
                            : {})), { htmlInput: {
                                "aria-label": getLocalizedMessage(language, "notificationsManager.trigger"),
                            } }), onChange: (event) => {
                            var _a, _b;
                            return onChangeNotification(Object.assign(Object.assign({}, notification), { triggers: automatedPresentation
                                    ? ((_b = (_a = triggerOptions.find((option) => option.value === event.target.value)) === null || _a === void 0 ? void 0 : _a.triggers) !== null && _b !== void 0 ? _b : notification.triggers)
                                    : [event.target.value] }));
                        }, children: triggerOptions.map(({ value, label }) => (jsx(MenuItem, { value: value, children: label }, value))) })] })), type === "scheduled" && notification.recipientDescription ? (jsxs(Box, { children: [jsx(FormLabel, { sx: { fontWeight: "bold", color: "#000000" }, children: getLocalizedMessage(language, "notificationsManager.recipients") }), jsx(Box, { sx: {
                            padding: "14px",
                            border: "1px solid #00000061",
                            color: "#00000061",
                        }, children: getScheduledRecipientDescription(notification, language) })] })) : (jsx(RecipientListSelect, { type: type, notification: notification, validationErrors: validationErrors, onChangeNotification: onChangeNotification, recipientLists: recipientLists, language: language })), jsxs(Box, { children: [jsxs(FormLabel, { sx: { marginBottom: "-6px", fontWeight: "bold", color: "#000000" }, children: [getLocalizedMessage(language, "notificationsManager.subject"), type === "normal" && "*"] }), jsx(TextField, { value: title, disabled: type === "scheduled" && !isNewScheduled, placeholder: getLocalizedMessage(language, "notificationsManager.subjectPlaceholder"), onBlur: () => onChangeNotification(Object.assign(Object.assign({}, notification), { title })), sx: {
                            marginBottom: 0,
                            marginTop: 0,
                        }, onChange: (e) => {
                            const value = e.target.value;
                            debouncedValidation("title", value, setValue, validationErrors !== null && validationErrors !== void 0 ? validationErrors : {}, 64);
                            setTitle(value);
                        }, fullWidth: true, margin: "normal", error: Boolean(validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.title"]), helperText: (_f = validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.title"]) === null || _f === void 0 ? void 0 : _f.message })] }), jsxs(Box, { children: [jsxs(FormLabel, { sx: { fontWeight: "bold", color: "#000000" }, children: [getLocalizedMessage(language, "notificationsManager.text"), type === "normal" && "*"] }), jsxs(Box, { sx: (theme) => (Object.assign(Object.assign({ border: `1px solid ${(validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.text"])
                                ? theme.palette.error.main
                                : "#00000061"}` }, ((validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.text"])
                            ? { color: "#00000061" }
                            : {})), { borderRadius: "8px", padding: "8px", marginBottom: supportedChannelsOnly ? 0 : "24px", backgroundColor: "#fff", "& ._toolbarRoot_uazmk_160": {
                                backgroundColor: "white",
                            } })), children: [jsx(MDXEditor, { markdown: text, placeholder: getLocalizedMessage(language, "notificationsManager.textPlaceholder"), onBlur: () => onChangeNotification(Object.assign(Object.assign({}, notification), { text })), onChange: (value) => {
                                    debouncedValidation("text", value, setValue, validationErrors !== null && validationErrors !== void 0 ? validationErrors : {}, 256);
                                    setText(value);
                                }, readOnly: type === "scheduled" && !isNewScheduled, plugins: [
                                    toolbarPlugin({
                                        toolbarContents: () => (jsxs(Fragment, { children: [jsx(UndoRedo, {}), jsx(BoldItalicUnderlineToggles, {}), jsx(ListsToggle, {})] })),
                                    }),
                                    headingsPlugin(),
                                    listsPlugin(),
                                    quotePlugin(),
                                ] }), (validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.text"]) && (jsx(FormHelperText, { sx: (theme) => ({ color: theme.palette.error.main }), children: (_h = (_g = validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.text"]) === null || _g === void 0 ? void 0 : _g.message) === null || _h === void 0 ? void 0 : _h.toString() }))] })] })] }));
};
/** Group presentation categories without substituting them for dispatch IDs. */
const getAutomatedTriggerOptions = (notification, availableTriggers, language) => {
    var _a, _b;
    const groups = new Map();
    // Include stored IDs so unavailable events remain visible, and a combined
    // template never loses a variant just because the available list is partial.
    for (const trigger of new Set([
        ...availableTriggers,
        ...((_a = notification.triggers) !== null && _a !== void 0 ? _a : []),
    ])) {
        const category = getAutomatedTriggerCategory(trigger);
        const triggers = (_b = groups.get(category)) !== null && _b !== void 0 ? _b : [];
        triggers.push(trigger);
        groups.set(category, triggers);
    }
    return [...groups]
        .map(([value, triggers]) => ({
        value,
        triggers,
        label: getAutomatedTriggerLabel(Object.assign(Object.assign({}, notification), { triggers }), language),
    }))
        .sort((a, b) => getAutomatedNotificationOrder(Object.assign(Object.assign({}, notification), { triggers: a.triggers })) -
        getAutomatedNotificationOrder(Object.assign(Object.assign({}, notification), { triggers: b.triggers })));
};
const getTriggerLabel = (trigger, language) => {
    const key = `notificationsManager.triggers.${trigger}`;
    const localizedLabel = getLocalizedMessage(language, key);
    return localizedLabel === key ? trigger : localizedLabel;
};
const getLocalizedMessageOrFallback$1 = (language, key, fallback) => {
    const localizedMessage = getLocalizedMessage(language, key);
    return localizedMessage === key ? fallback : localizedMessage;
};
const getScheduledRecipientDescription = (notification, language) => {
    var _a, _b;
    const fallback = (_a = notification.recipientDescription) !== null && _a !== void 0 ? _a : "";
    const trigger = (_b = notification.triggers) === null || _b === void 0 ? void 0 : _b[0];
    if (!trigger)
        return fallback;
    const key = `notificationsManager.recipientDescriptions.${trigger}`;
    if (fallback !== getLocalizedMessage("en", key))
        return fallback;
    const localizedDescription = getLocalizedMessage(language, key);
    return localizedDescription === key ? fallback : localizedDescription;
};
function RecipientListSelect({ type, notification, validationErrors, onChangeNotification, recipientLists, language, }) {
    var _a;
    const isNewScheduled = type === "scheduled" && isDraftNotification(notification);
    const recipientSelectId = useId();
    const labelId = `${recipientSelectId}-label`;
    const helperTextId = `${recipientSelectId}-helper-text`;
    const recipients = (_a = notification.recipients) === null || _a === void 0 ? void 0 : _a.split(",");
    const isFixedRecipientList = type === "normal" &&
        recipientLists.length === 1 &&
        (recipients === null || recipients === void 0 ? void 0 : recipients.length) === 1 &&
        recipients[0] === recipientLists[0].id;
    const recipientError = validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.recipients"];
    return (jsxs(FormControl, { children: [jsxs(FormLabel, { id: labelId, sx: { fontWeight: "bold", color: "#000000" }, children: [getLocalizedMessageOrFallback$1(language, "notificationsManager.recipientList", "Recipient list"), type === "normal" && "*"] }), jsx(Select, { labelId: labelId, "aria-describedby": (recipientError === null || recipientError === void 0 ? void 0 : recipientError.message) ? helperTextId : undefined, value: recipients, disabled: (type === "scheduled" && !isNewScheduled) || isFixedRecipientList, error: Boolean(recipientError), multiple: true, onChange: (e) => {
                    const value = e.target.value;
                    return onChangeNotification(Object.assign(Object.assign({}, notification), { recipients: value.join(",") }));
                }, children: recipientLists.map(({ id, name }) => (jsx(MenuItem, { value: id, children: name }, id))) }), (recipientError === null || recipientError === void 0 ? void 0 : recipientError.message) && (jsx(FormHelperText, { id: helperTextId, sx: (theme) => ({ color: theme.palette.error.main }), children: recipientError.message.toString() }))] }));
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce(fn, delay) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
}
const debouncedValidation = debounce((fieldName, value, setValue, validationErrors, maxLength) => {
    const fieldKey = `notification.${fieldName}`;
    const nextValidationErrors = Object.fromEntries(Object.entries(validationErrors).filter(([key]) => key !== fieldKey));
    setValue("validationErrors", nextValidationErrors);
    if (!value) {
        setValue("validationErrors", Object.assign(Object.assign({}, nextValidationErrors), { [fieldKey]: {
                type: "required",
                message: "This field is required",
            } }));
    }
    if (value.length > maxLength) {
        setValue("validationErrors", Object.assign(Object.assign({}, nextValidationErrors), { [fieldKey]: {
                type: "maxLength",
                message: `Must be less than ${maxLength} characters`,
            } }));
    }
}, 250);

const NotificationList = ({ notifications, type, handleDelete, handleEdit, handleCreate, availableTriggers = [], recipientLists, validationErrors, hasTeamsChannel, hasEmailChannel, autoPublishOnSave = false, oneTimeNotificationLifecycle = false, language, }) => {
    var _a;
    const [notificationToDelete, setNotificationToDelete] = useState(null);
    const hasError = !!((validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.channels"]) ||
        (validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.title"]) ||
        (validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.recipients"]) ||
        (validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors["notification.text"]));
    const automatedPresentation = type === "scheduled" && oneTimeNotificationLifecycle;
    const displayedNotifications = automatedPresentation
        ? [...notifications].sort((a, b) => getAutomatedNotificationOrder(a) - getAutomatedNotificationOrder(b))
        : notifications;
    const onChangeNotification = (notification) => {
        handleEdit(notification, type);
    };
    const handleConfirmDelete = () => {
        if (!notificationToDelete) {
            return;
        }
        handleDelete(notificationToDelete, type);
        setNotificationToDelete(null);
    };
    const handleCloseDelete = () => {
        setNotificationToDelete(null);
    };
    return (jsxs(Box$1, { sx: {
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            mt: 2,
        }, children: [jsxs(Box$1, { display: "flex", alignItems: "center", justifyContent: "space-between", children: [jsx(Typography, { sx: {
                            color: "#000",
                            fontSize: "16px",
                            fontWeight: 500,
                        }, children: type === "normal"
                            ? getLocalizedMessage(language, "notificationsManager.oneTimeNotifications")
                            : getLocalizedMessage(language, "notificationsManager.scheduledNotifications") }), type === "scheduled" && handleCreate && (jsx(Button, { variant: "outlined", startIcon: jsx(Add, {}), onClick: handleCreate, children: getLocalizedMessage(language, "notificationsManager.newScheduledNotification") }))] }), jsxs(Paper, { sx: {
                    borderRadius: 3,
                    boxShadow: "none",
                    border: "1px solid",
                    borderColor: grey[300],
                }, children: [jsx(Grid2, { container: true, alignItems: "center", sx: {
                            py: 2,
                            paddingLeft: 2,
                            borderBottom: "1px solid",
                            borderColor: grey[300],
                            height: "69px",
                        }, children: jsx(Grid2, { size: 8.5, sx: { fontSize: "14px", fontWeight: 500 }, children: getLocalizedMessage(language, "notificationsManager.notificationSubject") }) }), jsx(Box$1, { sx: {
                            overflowY: "auto",
                            width: "100%",
                        }, children: displayedNotifications.map((notification) => {
                            var _a;
                            const scheduledTriggerLabel = type === "scheduled"
                                ? automatedPresentation
                                    ? getAutomatedTriggerLabel(notification, language)
                                    : getScheduledTriggerLabel(notification, language)
                                : undefined;
                            return (jsxs(Accordion, { sx: {
                                    boxShadow: "none",
                                    "&:last-of-type": {
                                        borderRadius: "12px",
                                    },
                                }, defaultExpanded: isDraftNotification(notification), children: [jsx(AccordionSummary, { sx: { paddingLeft: 0 }, expandIcon: jsx(ExpandMore, {}), "aria-controls": "panel1-content", id: "panel1-header", children: jsxs(Box$1, { sx: Object.assign({ marginX: "24px", display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", minHeight: "40px" }, (automatedPresentation && {
                                                minWidth: 0,
                                                gap: 2,
                                                flexWrap: "wrap",
                                            })), children: [jsxs(Box$1, { sx: Object.assign({ display: "flex", flexDirection: "column" }, (automatedPresentation && {
                                                        flex: "1 1 200px",
                                                        minWidth: 0,
                                                        overflowWrap: "anywhere",
                                                    })), children: [jsx(Typography, { children: notification.title }), scheduledTriggerLabel && (jsx(Typography, { variant: "caption", color: "text.secondary", children: scheduledTriggerLabel }))] }), jsxs(Box$1, { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "12px", sx: automatedPresentation
                                                        ? {
                                                            flexShrink: 0,
                                                            flexWrap: "wrap",
                                                            maxWidth: "100%",
                                                        }
                                                        : undefined, children: [notification.channels && (jsx(Chip, { backgroundColor: "#EBE3F4", color: "#6333A8", label: displayChannels(notification.channels, language, oneTimeNotificationLifecycle) })), !autoPublishOnSave && (jsx(PublishedAtChip, { notification: notification, language: language, automated: automatedPresentation })), !(type === "normal" && oneTimeNotificationLifecycle) && (jsx(AntSwitch, { color: "success", checked: (_a = notification.active) !== null && _a !== void 0 ? _a : false, onClick: (e) => {
                                                                e.stopPropagation();
                                                                onChangeNotification(Object.assign(Object.assign({}, notification), { active: !notification.active }));
                                                            } })), jsx(IconButton, { "aria-label": `Delete ${notification.title || "notification"}`, color: "error", onClick: (event) => {
                                                                event.stopPropagation();
                                                                setNotificationToDelete(notification);
                                                            }, size: "small", children: jsx(DeleteOutlined, {}) }), hasError && (jsx(Grid2, { sx: (theme) => ({
                                                                fontSize: "14px",
                                                                color: theme.palette.error.main,
                                                                marginRight: "16px",
                                                            }), size: 0.5, children: jsx(Error$1, {}) }))] })] }) }), type === "normal" ? (jsx(NotificationForm, { notification: notification, type: type, onChangeNotification: onChangeNotification, recipientLists: recipientLists, validationErrors: validationErrors, hasTeamsChannel: hasTeamsChannel, hasEmailChannel: hasEmailChannel, oneTimeNotificationLifecycle: oneTimeNotificationLifecycle, language: language })) : (jsx(NotificationForm, { notification: notification, type: type, onChangeNotification: onChangeNotification, recipientLists: recipientLists, hasTeamsChannel: hasTeamsChannel, hasEmailChannel: hasEmailChannel, availableTriggers: availableTriggers, oneTimeNotificationLifecycle: oneTimeNotificationLifecycle, language: language }))] }, notification.id));
                        }) })] }), jsxs(Dialog, { open: notificationToDelete != null, onClose: handleCloseDelete, children: [jsx(DialogTitle, { children: "Confirm Delete" }), jsx(DialogContent, { children: jsx(Typography, { children: getLocalizedMessage(language, oneTimeNotificationLifecycle &&
                                type === "normal" &&
                                ((_a = notificationToDelete === null || notificationToDelete === void 0 ? void 0 : notificationToDelete.channels) === null || _a === void 0 ? void 0 : _a.includes(NotificationChannel.BANNER)) &&
                                notificationToDelete.published === true
                                ? "notificationsManager.deletePublishedBannerConfirmation"
                                : "notificationsManager.deleteConfirmation") }) }), jsxs(DialogActions, { children: [jsx(Button, { onClick: handleCloseDelete, children: "Cancel" }), jsx(Button, { variant: "contained", color: "error", onClick: handleConfirmDelete, children: getLocalizedMessageOrFallback(language, "dialogActions.delete", "Delete") })] })] })] }));
};
const PublishedAtChip = ({ notification: { lastSentAt, published, active }, language, automated = false, }) => {
    const activePublished = active && published;
    const automatedStatus = !active
        ? "deactivated"
        : published
            ? "active"
            : "draft";
    if (published || automated) {
        return (jsxs(Box$1, { sx: {
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 0.5,
            }, children: [jsx(Chip, { backgroundColor: automated
                        ? activePublished
                            ? "success.light"
                            : "grey.200"
                        : "#E1F1E7", color: automated
                        ? activePublished
                            ? "success.dark"
                            : "text.secondary"
                        : "#1C904C", label: getLocalizedMessage(language, automated
                        ? `notificationsManager.automatedStatus.${automatedStatus}`
                        : "notificationsManager.published") }), lastSentAt && (jsx(Typography, { variant: "caption", color: "text.secondary", children: getLocalizedMessage(language, "notificationsManager.lastSent", {
                        date: dayjs(lastSentAt).format(language === "de" ? "DD.MM.YY" : "MM/DD/YY"),
                    }) }))] }));
    }
    return (jsx(Chip, { label: getLocalizedMessageOrFallback(language, "notificationsManager.notPublished", "Not Published"), backgroundColor: "#E1F1E7", color: "grey" }));
};
const getScheduledTriggerLabel = (notification, language) => {
    var _a;
    if (!("triggers" in notification)) {
        return undefined;
    }
    const trigger = (_a = notification.triggers) === null || _a === void 0 ? void 0 : _a[0];
    if (!trigger) {
        return undefined;
    }
    const key = `notificationsManager.triggers.${trigger}`;
    const label = getLocalizedMessage(language, key);
    return label === key ? undefined : label;
};
const getLocalizedMessageOrFallback = (language, key, fallback) => {
    const localizedMessage = getLocalizedMessage(language, key);
    return localizedMessage === key ? fallback : localizedMessage;
};
const Chip = ({ label, color, backgroundColor, }) => {
    return (jsx(Typography, { sx: {
            fontWeight: 500,
            borderRadius: "12px",
            paddingX: "18px",
            paddingY: "2px",
            backgroundColor,
            color,
        }, children: label }));
};
const displayChannels = (channels, language, hideInternalMembership = false) => {
    const displayedChannels = hideInternalMembership &&
        channels.some((channel) => channel === NotificationChannel.BANNER ||
            channel === NotificationChannel.EMAIL)
        ? channels.filter((channel) => channel !== NotificationChannel.NOTIFICATION)
        : channels;
    return displayedChannels.reduce((acc, channel) => {
        var _a;
        if (acc.length > 0) {
            acc += ", ";
        }
        acc += getLocalizedMessageOrFallback(language, `notificationsManager.channel.${channel}`, (_a = channelLabels[channel]) !== null && _a !== void 0 ? _a : channel);
        return acc;
    }, "");
};
const channelLabels = {
    [NotificationChannel.BANNER]: "Banner",
    [NotificationChannel.EMAIL]: "Email",
    [NotificationChannel.NOTIFICATION]: "Notification",
    [NotificationChannel.TEAMS]: "Microsoft Teams",
};

const NotificationsManager = ({ notifications, scheduledNotifications, recipientLists = [], fireEvent, validationErrors, hasTeamsChannel, hasEmailChannel, autoPublishOnSave = false, oneTimeNotificationLifecycle = false, language, }) => {
    const handleEdit = (notification, type) => {
        notification.dirty = true;
        notification.published = autoPublishOnSave;
        if (isScheduledNotification(notification, type)) {
            const index = (scheduledNotifications !== null && scheduledNotifications !== void 0 ? scheduledNotifications : []).findIndex((noti) => noti.id === notification.id);
            if (index === -1) {
                return;
            }
            fireEvent === null || fireEvent === void 0 ? void 0 : fireEvent("notificationsUpdated", {
                scheduledNotifications: (scheduledNotifications !== null && scheduledNotifications !== void 0 ? scheduledNotifications : []).map((noti, i) => {
                    if (i === index) {
                        return notification;
                    }
                    return noti;
                }),
            });
        }
        else {
            const index = notifications.findIndex((noti) => noti.id === notification.id);
            if (index === -1) {
                return;
            }
            fireEvent === null || fireEvent === void 0 ? void 0 : fireEvent("notificationsUpdated", {
                notifications: notifications.map((noti, i) => {
                    if (i === index) {
                        return notification;
                    }
                    return noti;
                }),
            });
        }
    };
    const handleDelete = (notification, type) => {
        fireEvent === null || fireEvent === void 0 ? void 0 : fireEvent("notificationDeleted", {
            notificationToDelete: { notification, type },
        });
    };
    const handleCreateScheduled = () => {
        fireEvent === null || fireEvent === void 0 ? void 0 : fireEvent("notificationsUpdated", {
            scheduledNotifications: [
                ...(scheduledNotifications !== null && scheduledNotifications !== void 0 ? scheduledNotifications : []),
                {
                    id: createDraftNotificationId(),
                    title: "",
                    text: "",
                    triggers: [],
                    recipients: "",
                    channels: [
                        NotificationChannel.BANNER,
                        NotificationChannel.NOTIFICATION,
                        ...(hasEmailChannel ? [NotificationChannel.EMAIL] : []),
                        ...(hasTeamsChannel ? [NotificationChannel.TEAMS] : []),
                    ],
                    source: "schedule",
                    active: true,
                    published: true,
                    dirty: true,
                },
            ],
        });
    };
    return (jsxs(Box, { sx: {
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "calc(100vh - 83px)",
            overflowY: "auto",
        }, children: [jsx(Typography, { sx: { fontSize: "24px", fontWeight: 500 }, children: getLocalizedMessage(language, "notificationsManager.title") }), jsx(NotificationList, { notifications: notifications, type: "normal", handleDelete: handleDelete, handleEdit: (notification) => handleEdit(notification, "normal"), recipientLists: recipientLists, validationErrors: validationErrors, hasTeamsChannel: hasTeamsChannel, hasEmailChannel: hasEmailChannel, autoPublishOnSave: autoPublishOnSave, oneTimeNotificationLifecycle: oneTimeNotificationLifecycle, language: language }), jsx(NotificationList, { notifications: scheduledNotifications !== null && scheduledNotifications !== void 0 ? scheduledNotifications : [], type: "scheduled", handleCreate: handleCreateScheduled, handleDelete: handleDelete, handleEdit: (notification) => handleEdit(notification, "scheduled"), availableTriggers: (scheduledNotifications !== null && scheduledNotifications !== void 0 ? scheduledNotifications : []).reduce((triggers, notification) => {
                    var _a;
                    return [
                        ...triggers,
                        ...((_a = notification.triggers) !== null && _a !== void 0 ? _a : []),
                    ];
                }, []), recipientLists: recipientLists, hasTeamsChannel: hasTeamsChannel, hasEmailChannel: hasEmailChannel, oneTimeNotificationLifecycle: oneTimeNotificationLifecycle, language: language }), autoPublishOnSave && (jsx(Box, { sx: { width: "100%", display: "flex", justifyContent: "flex-end" }, children: jsx(PMButton, { onClick: () => {
                        fireEvent === null || fireEvent === void 0 ? void 0 : fireEvent("savePmoNotification", {
                            notifications: notifications,
                            scheduledNotifications: scheduledNotifications,
                        });
                    }, text: "Save", size: "medium", variant: "contained" }) }))] }));
};

class NotificationsPlugin {
    /**
     * @param _strapiBaseUrl - base URL of Strapi backend
     * @param _hasTeamsChannel - Should the Teams notification channel be shown
     * @param _hasEmailChannel - Should the Email notification channel be shown
     * @param _autoPublishOnSave - Skip the separate publish step and mark a
     *   notification published as soon as it is saved (PMO has no publish
     *   workflow of its own; other products publish as a distinct action).
     * @param _interval - interval in milliseconds to check for new notifications
     * @param _oneTimeNotificationLifecycle - Opt into backend-owned one-time
     *   lifecycle: no active switch, Banner/Email editor options and automatic
     *   Banner bell membership. Scheduled editor options remain unchanged.
     */
    constructor(_strapiBaseUrl, _hasTeamsChannel = false, _hasEmailChannel = true, _autoPublishOnSave = false, _interval = 30000, _oneTimeNotificationLifecycle = false) {
        this._strapiBaseUrl = _strapiBaseUrl;
        this._hasTeamsChannel = _hasTeamsChannel;
        this._hasEmailChannel = _hasEmailChannel;
        this._autoPublishOnSave = _autoPublishOnSave;
        this._interval = _interval;
        this._oneTimeNotificationLifecycle = _oneTimeNotificationLifecycle;
    }
    onRegister(app) {
        this.app = app;
        app.patchAppState({
            banner: undefined,
            allNotificationsForUser: [],
        });
        app.on("init", () => {
            void this.fetchUnreadNotificationsIntoAppState();
            setInterval(() => void this.fetchUnreadNotificationsIntoAppState(), this._interval);
        });
    }
    fetchUnreadNotificationsIntoAppState() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const unreadNotifications = yield this.fetchUnreadNotifications();
            // Split notifications by channel type
            const bannerNotifications = unreadNotifications.filter((n) => { var _a; return (_a = n.channels) === null || _a === void 0 ? void 0 : _a.some((channel) => channel === NotificationChannel.BANNER); });
            const regularNotifications = unreadNotifications.filter((n) => {
                var _a;
                return (_a = n.channels) === null || _a === void 0 ? void 0 : _a.some((channel) => channel === NotificationChannel.NOTIFICATION ||
                    (this._oneTimeNotificationLifecycle &&
                        channel === NotificationChannel.BANNER));
            });
            const latestBanner = bannerNotifications.length > 0
                ? bannerNotifications.sort((n1, n2) => { var _a, _b, _c; return (_c = (_a = n2.createdAt) === null || _a === void 0 ? void 0 : _a.localeCompare((_b = n1.createdAt) !== null && _b !== void 0 ? _b : "")) !== null && _c !== void 0 ? _c : 1; })[0]
                : undefined;
            (_a = this.app) === null || _a === void 0 ? void 0 : _a.patchAppState({
                banner: latestBanner,
                allNotificationsForUser: regularNotifications,
            });
        });
    }
    fetchAllNotifications() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/all`, {
                credentials: "include",
            });
            return (yield response.json());
        });
    }
    fetchAllNotificationsIntoAppState() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const allNotifications = yield this.fetchAllNotifications();
            (_a = this.app) === null || _a === void 0 ? void 0 : _a.patchAppState({
                allNotifications,
            });
        });
    }
    fetchAllNotificationsForUserIntoAppState() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const allNotificationsForUser = yield this.fetchAllNotificationsForUser();
            (_a = this.app) === null || _a === void 0 ? void 0 : _a.patchAppState({
                allNotificationsForUser,
            });
        });
    }
    fetchAllNotificationsForUser() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications`, {
                credentials: "include",
            });
            return (yield response.json());
        });
    }
    fetchScheduledNotifications() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/scheduled`, {
                credentials: "include",
            });
            return (yield response.json());
        });
    }
    fetchScheduledNotificationsIntoAppState() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const scheduledNotifications = yield this.fetchScheduledNotifications();
            (_a = this.app) === null || _a === void 0 ? void 0 : _a.patchAppState({
                scheduledNotifications: scheduledNotifications,
            });
        });
    }
    fetchUnreadNotifications() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/unread`, {
                    credentials: "include",
                });
                if (!response.ok) {
                    return [];
                }
                const json = (yield response.json());
                return Array.isArray(json) ? json : [];
            }
            catch (_a) {
                return [];
            }
        });
    }
    fetchReadNotifications() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/read`, {
                credentials: "include",
            });
            return (yield response.json());
        });
    }
    fetchRecipientListsIntoAppState() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const recipientLists = yield this.fetchRecipientLists();
            (_a = this.app) === null || _a === void 0 ? void 0 : _a.patchAppState({
                notificationRecipientLists: recipientLists,
            });
        });
    }
    fetchRecipientLists() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/recipient-lists`, {
                    credentials: "include",
                });
                return (yield response.json());
            }
            catch (_a) {
                return [];
            }
        });
    }
    setNotificationToRead(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/${notificationId}/read`, {
                method: "POST",
                credentials: "include",
            });
        });
    }
    setNotificationToUnread(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/${notificationId}/unread`, {
                method: "POST",
                credentials: "include",
            });
        });
    }
    createNotification(notification) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(notification),
            });
        });
    }
    createScheduledNotification(notification) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/scheduled`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(notification),
            });
        });
    }
    deleteNotification(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/${notificationId}`, {
                method: "DELETE",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
            });
        });
    }
    deleteScheduledNotification(notificationId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/scheduled/${notificationId}`, {
                method: "DELETE",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
            });
        });
    }
    updateNotification(notificationId, notification) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/${notificationId}`, {
                method: "PUT",
                credentials: "include",
                body: JSON.stringify(notification),
                headers: {
                    "Content-Type": "application/json",
                },
            });
        });
    }
    updateScheduledNotification(notificationId, notification) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/scheduled/${notificationId}`, {
                method: "PUT",
                credentials: "include",
                body: JSON.stringify(notification),
                headers: {
                    "Content-Type": "application/json",
                },
            });
        });
    }
    updateNotifications(notifications) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/`, {
                method: "PUT",
                credentials: "include",
                body: JSON.stringify(notifications),
                headers: {
                    "Content-Type": "application/json",
                },
            });
        });
    }
    updateScheduledNotifications(notifications) {
        return __awaiter(this, void 0, void 0, function* () {
            yield fetch(`${this._strapiBaseUrl}/pimo-strapi-notifications-plugin/notifications/scheduled`, {
                method: "PUT",
                credentials: "include",
                body: JSON.stringify(notifications),
                headers: {
                    "Content-Type": "application/json",
                },
            });
        });
    }
    getName() {
        return "NotificationsPlugin";
    }
    handleOverlayNotificationReadEvent(_a) {
        return __awaiter(this, arguments, void 0, function* ({ payload, }) {
            var _b, _c, _d, _e, _f, _g;
            const { notification } = payload;
            if (!(notification === null || notification === void 0 ? void 0 : notification.id))
                return;
            yield this.setNotificationToRead(notification.id);
            if ((_b = notification.channels) === null || _b === void 0 ? void 0 : _b.some((channel) => channel === NotificationChannel.BANNER)) {
                // Scheduled notifications (e.g. deadline reminders) can create one
                // banner notification per recipient-affecting item (e.g. per OE/
                // project), all sharing the same title. Without this, dismissing one
                // just reveals the next with the same title, forcing the user to
                // close the banner repeatedly. Mark all of them read at once so the
                // banner disappears for good after a single dismissal.
                const unreadNotifications = yield this.fetchUnreadNotifications();
                const duplicateBannerNotifications = unreadNotifications.filter((n) => {
                    var _a;
                    return n.id != null &&
                        n.id !== notification.id &&
                        n.title === notification.title &&
                        ((_a = n.channels) === null || _a === void 0 ? void 0 : _a.some((channel) => channel === NotificationChannel.BANNER));
                });
                yield Promise.all(duplicateBannerNotifications.map((n) => this.setNotificationToRead(n.id)));
                (_c = this.app) === null || _c === void 0 ? void 0 : _c.patchAppState({ banner: undefined });
                void this.fetchUnreadNotificationsIntoAppState();
            }
            else if ((_d = notification.channels) === null || _d === void 0 ? void 0 : _d.some((channel) => channel === NotificationChannel.NOTIFICATION)) {
                const currentNotifications = (_f = (_e = this.app) === null || _e === void 0 ? void 0 : _e.getAppState().allNotificationsForUser) !== null && _f !== void 0 ? _f : [];
                const updatedNotifications = currentNotifications.filter((n) => n.id !== notification.id);
                (_g = this.app) === null || _g === void 0 ? void 0 : _g.patchAppState({
                    allNotificationsForUser: updatedNotifications,
                });
            }
        });
    }
    renderNotificationsManager({ view, route, layoutProps, }) {
        const notificationsManager = view.addComponent({
            component: NotificationsManager,
            layoutProps,
        });
        notificationsManager.mapState(({ allNotifications, scheduledNotifications, notificationRecipientLists, editSettingsForm, language, }) => {
            var _a;
            return {
                notifications: (allNotifications !== null && allNotifications !== void 0 ? allNotifications : []).filter((notification) => notification.source === "user"),
                scheduledNotifications: scheduledNotifications !== null && scheduledNotifications !== void 0 ? scheduledNotifications : [],
                recipientLists: notificationRecipientLists !== null && notificationRecipientLists !== void 0 ? notificationRecipientLists : [],
                validationErrors: (_a = editSettingsForm === null || editSettingsForm === void 0 ? void 0 : editSettingsForm.validationErrors) !== null && _a !== void 0 ? _a : {},
                hasTeamsChannel: this._hasTeamsChannel,
                hasEmailChannel: this._hasEmailChannel,
                autoPublishOnSave: this._autoPublishOnSave,
                oneTimeNotificationLifecycle: this._oneTimeNotificationLifecycle,
                language,
            };
        });
        notificationsManager.on("notificationDeleted", (_a) => __awaiter(this, [_a], void 0, function* ({ payload }) {
            var _b, _c, _d;
            if (!(payload === null || payload === void 0 ? void 0 : payload.notificationToDelete)) {
                return;
            }
            const { type, notification } = payload.notificationToDelete;
            if ((notification === null || notification === void 0 ? void 0 : notification.id) == null) {
                return;
            }
            if (isDraftNotification(notification)) {
                (_b = this.app) === null || _b === void 0 ? void 0 : _b.patchAppState({
                    scheduledNotifications: ((_d = (_c = this.app) === null || _c === void 0 ? void 0 : _c.getAppState().scheduledNotifications) !== null && _d !== void 0 ? _d : []).filter(({ id }) => id !== notification.id),
                });
                return;
            }
            if (type === "normal") {
                yield this.deleteNotification(notification.id);
                yield this.fetchAllNotificationsIntoAppState();
            }
            else if (type === "scheduled") {
                yield this.deleteScheduledNotification(notification.id);
                yield this.fetchScheduledNotificationsIntoAppState();
            }
        }));
        notificationsManager.on("savePmoNotification", (_a) => __awaiter(this, [_a], void 0, function* ({ payload }) {
            var _b;
            if (!payload) {
                return;
            }
            // We dont have a publishing mechanic in PMO. So the pmo notification is always published.
            if ((_b = payload.notifications) === null || _b === void 0 ? void 0 : _b[0]) {
                payload.notifications[0].published = true;
                yield this.updateNotifications(payload.notifications);
            }
            if (payload.scheduledNotifications) {
                const newScheduledNotifications = payload.scheduledNotifications.filter(isDraftNotification);
                const existingScheduledNotifications = payload.scheduledNotifications.filter((notification) => !isDraftNotification(notification));
                yield Promise.all(newScheduledNotifications.map((notification) => this.createScheduledNotification(Object.assign(Object.assign({}, notification), { id: undefined, published: true }))));
                if (existingScheduledNotifications.length > 0) {
                    yield this.updateScheduledNotifications(existingScheduledNotifications);
                }
                yield this.fetchScheduledNotificationsIntoAppState();
            }
        }));
        notificationsManager.on("notificationsUpdated", (_a) => __awaiter(this, [_a], void 0, function* ({ payload }) {
            var _b, _c, _d, _e, _f;
            let notifications = payload === null || payload === void 0 ? void 0 : payload.notifications;
            const scheduledNotifications = payload === null || payload === void 0 ? void 0 : payload.scheduledNotifications;
            const state = (_b = this.app) === null || _b === void 0 ? void 0 : _b.getAppState();
            if (notifications) {
                notifications = notifications.map(withoutDraftId);
                const validationErrors = Object.assign({}, (_c = state === null || state === void 0 ? void 0 : state.editSettingsForm) === null || _c === void 0 ? void 0 : _c.validationErrors);
                if (this._oneTimeNotificationLifecycle) {
                    // Persisted alert-only rows remain valid until deliberately changed.
                    // Internal bell membership is not a selectable delivery option.
                    const invalidDraft = notifications.some((notification) => {
                        var _a;
                        return !notification.id &&
                            !((_a = notification.channels) === null || _a === void 0 ? void 0 : _a.some((channel) => channel === NotificationChannel.BANNER ||
                                channel === NotificationChannel.EMAIL));
                    });
                    if (invalidDraft) {
                        validationErrors["notification.channels"] = {
                            type: "required",
                            tabContainingErrors: "basic-notifications",
                            message: getLocalizedMessage((_d = state === null || state === void 0 ? void 0 : state.language) !== null && _d !== void 0 ? _d : "en", "notificationsManager.supportedChannelRequired"),
                        };
                    }
                    else {
                        delete validationErrors["notification.channels"];
                    }
                }
                (_e = this.app) === null || _e === void 0 ? void 0 : _e.patchAppState({
                    allNotifications: notifications,
                    changedNotifications: notifications.filter((notification) => notification.dirty),
                    editSettingsForm: Object.assign(Object.assign(Object.assign({}, state === null || state === void 0 ? void 0 : state.editSettingsForm), (this._oneTimeNotificationLifecycle ? { validationErrors } : {})), { enableSaveButton: true }),
                });
            }
            if (scheduledNotifications) {
                (_f = this.app) === null || _f === void 0 ? void 0 : _f.patchAppState({
                    scheduledNotifications: scheduledNotifications,
                    changedScheduledNotifications: scheduledNotifications.filter((notification) => notification.dirty),
                    editSettingsForm: Object.assign(Object.assign({}, state === null || state === void 0 ? void 0 : state.editSettingsForm), { enableSaveButton: true }),
                });
            }
        }));
        route.on("load", () => {
            void this.fetchAllNotificationsIntoAppState();
            void this.fetchScheduledNotificationsIntoAppState();
            void this.fetchRecipientListsIntoAppState();
        });
    }
}

export { NotificationChannel, NotificationsPlugin, createDraftNotificationId, isDraftNotification, isScheduledNotification, withoutDraftId };
